import Scene = Phaser.Scene;
import Text = Phaser.GameObjects.Text;
import Image = Phaser.GameObjects.Image;
import Container = Phaser.GameObjects.Container;
import Sprite = Phaser.GameObjects.Sprite;
import {Sizes} from "@/constants/size";
import {Keys} from "@/constants/key";
import {CreatureUtils} from "@/utils/creature-utils";
import {Creature, CreatureEffectType, Force, Skill, WarriorClass} from "@/store/level/types";
import TextUtils from "@/utils/text-utils";
import SkillUtils from "@/utils/skill-utils";
import {SkillType} from "@/store/place/types";
import {Effect} from "@/store/battle/types";
import AtlasUtils from "@/utils/atlas-utils";
import {StatUtils} from "@/utils/stat-utils";
import StatPanelUtils from "@/utils/stat-panel-utils";
import SpriteUtils from "@/utils/sprite-utils";
import CU from "../utils/c-u";
import SContainer from "@/cmp/s-container";
import {PositionedContainer} from "@/scenes.ui/positioned-container";
import SoundUtils from "@/utils/sound-utils";
import {Settings} from "@/constants/settings";

export default class CreaturePanel extends SContainer implements PositionedContainer {

    static readonly PLAYER_CARD_BORDER_NAME = 'playerCardBorder';
    static readonly ENEMY_CARD_BORDER_NAME = 'enemyCardBorder';

    private readonly HEALTH_BAR_WIDTH: number = 59;
    private readonly HEALTH_BAR_HEIGHT: number = 8;

    private healthText!: Text;
    private onDeadCallback!: () => unknown;

    private playerCardBorder!: Image;
    private enemyCardBorder!: Image;

    private callback: (() => unknown) | undefined = undefined;

    private mainImage: Image | undefined;

    private attackValueContainer: Container | undefined;
    private defenceValueContainer: Container | undefined;

    private attackValue: Text | undefined;
    private defenceValue: Text | undefined;

    private healthBarInner: Image | undefined;
    private healthBarOuter: Image | undefined;

    private koscheyBarInner: Image | undefined;
    private koscheyBarOuter: Image | undefined;
    private koscheyShell: Image | undefined;
    private koscheyValue: Text | undefined;

    private fireShell: Image | undefined;
    private fireValue: Text | undefined;

    private gorgonShell: Image | undefined;

    private poisonPotion: Image | undefined;
    private poisonValue: Text | undefined;

    private creature!: Creature;
    private isPlayerCreature!: boolean;

    private attackValueIcon!: Image;
    private defenceValueIcon!: Image;

    private cardShade!: Image;

    private skull: Sprite | undefined;

    private defenderLook: boolean = false;

    private infiniteParams: boolean = false;

    private rankIcon: Image | undefined;

    private spriteByForce: Map<WarriorClass, Sprite> = new Map<WarriorClass, Phaser.GameObjects.Sprite>();

    private healthVisible: boolean = false;
    private koscheyActive: boolean = false;
    private isLookByConsumable: boolean = false;

    private originalX: number = 0;
    private originalY: number = 0;

    private kubEffect: Sprite | undefined;

    constructor(scene: Scene, x: number, y: number, c: Creature, onDeadCallback: () => unknown, isPlayerCreature: boolean = false,
                showStat: boolean = true) {

        super(scene, x, y);
        this.isPlayerCreature = isPlayerCreature;

        this.onDeadCallback = onDeadCallback;
        this.setPanelCombative(c.combative);

        this.setSize(Sizes.CELL_WIDTH, Sizes.CELL_HEIGHT);

        this.cardShade = SpriteUtils.addImage(this.scene, -38 / CU.S, 28 / CU.S, Keys.IMG_CARD_SHADE, undefined, 1 / CU.S, 1 / CU.S)
            .setVisible(false);

        if (Keys.ADDITIONAL_CREATURES.includes(c.imageId)) {
            this.mainImage = SpriteUtils.addImage(this.scene, 0, 0, Keys.battleCreature(c.imageId), undefined, 1 / CU.S, 1 / CU.S);
        } else {
            this.mainImage = SpriteUtils.addImage(this.scene, 0, 0, AtlasUtils.getCreatureBattleAtlas(c.imageId), c.imageId, 1 / CU.S, 1 / CU.S);
        }

        this.infiniteParams = c.warriorClass === WarriorClass.NORN;
        let healthVal = SkillUtils.formatHealth(CreatureUtils.getUnitHealthCurrentValue(c));

        this.healthText = TextUtils.addText(this.scene, healthVal, 0, 72 / CU.S,
            '#ffffff', 12 / CU.S, 'normal', 80).setVisible(false);

        if (isPlayerCreature) {
            this.attackValueIcon = SpriteUtils.addImage(this.scene, 18, 0, Keys.ATLAS_UI, Keys.UI_CARD_ATTACK_ICON_GOLD);

            this.defenceValueIcon = SpriteUtils.addImage(this.scene, 6,1, Keys.ATLAS_UI, Keys.UI_CARD_DEFENCE_ICON_GOLD);

        } else {
            this.attackValueIcon = SpriteUtils.addImage(this.scene, 18, 0, Keys.ATLAS_UI, Keys.UI_CARD_ATTACK_ICON_GREY);

            this.defenceValueIcon = SpriteUtils.addImage(this.scene, 6,1, Keys.ATLAS_UI, Keys.UI_CARD_DEFENCE_ICON_GREY);
        }

        let attVal = SkillUtils.formatValue(CreatureUtils.getUnitAttackValue(c))
        let defVal = SkillUtils.formatValue(CreatureUtils.getUnitDefenceValue(c));

        if (this.infiniteParams) {
            attVal = '∞';
            defVal = '∞';
        }

        let fntSize = (attVal.length > 3 || defVal.length > 3) ? 10 : 11;

        this.attackValue = TextUtils.addText(this.scene, attVal, this.attackValueIcon.x / CU.S,
            this.attackValueIcon.y / CU.S + 2,'#fefffa', fntSize, 'bold', 60).setOrigin(0.5, 0.5);

        this.defenceValue = TextUtils.addText(this.scene, defVal, this.defenceValueIcon.x / CU.S,
            this.defenceValueIcon.y / CU.S + 1,'#fefffa', fntSize, 'bold', 60).setOrigin(0.5, 0.5);

        this.attackValueContainer = this.scene.add.container(-45, 50, [this.attackValueIcon, this.attackValue]);
        this.defenceValueContainer = this.scene.add.container(15, 50, [this.defenceValueIcon, this.defenceValue]);

        this.playerCardBorder = SpriteUtils.addSpriteN(this.scene, 0, 0, Keys.ATLAS_UI, Keys.IMG_GREEN_CONTOUR, 1 / CU.S, 1 / CU.S)
            .setName(CreaturePanel.PLAYER_CARD_BORDER_NAME)
            .setVisible(false);

        this.enemyCardBorder = SpriteUtils.addSpriteN(this.scene, 0, 0, Keys.ATLAS_UI, Keys.IMG_RED_CONTOUR, 1 / CU.S, 1 / CU.S)
            .setName(CreaturePanel.ENEMY_CARD_BORDER_NAME)
            .setVisible(false);

        this.healthBarInner = SpriteUtils.addSpriteN(this.scene, 0, 77, Keys.ATLAS_UI, Keys.UI_HEALTH_BAR_INNER, 1 / CU.S, 1 / CU.S)
            .setVisible(false);

        let health = CreatureUtils.getUnitHealth(c);

        this.healthBarOuter = SpriteUtils.addSpriteN(this.scene, this.healthBarInner.x - 30, this.healthBarInner.y, Keys.ATLAS_UI, Keys.UI_HEALTH_BAR_OUTER, 1, 1)
            .setOrigin(0, 0.5)
            .setVisible(false);
        SpriteUtils.setDispSize(this.healthBarOuter, health.valueCurrent == health.valueTotal ? this.HEALTH_BAR_WIDTH : health.valueCurrent / health.valueTotal * this.HEALTH_BAR_WIDTH, this.HEALTH_BAR_HEIGHT);


        this.add([this.cardShade, this.mainImage, this.healthBarInner, this.healthBarOuter, this.healthText,
            this.attackValueContainer, this.defenceValueContainer,
            this.playerCardBorder, this.enemyCardBorder]);

        this.bringToTop(this.attackValueContainer);
        this.bringToTop(this.defenceValueContainer);

        this.creature = c;

        if (showStat) {
            this.rankIcon = scene.add.image(CreatureUtils.isPremium(c) ? -1 : 0, (CreatureUtils.isPremium(c) ? -this.height / 2 + 10 : -this.height / 2 + 17), '');
            this.setupRankSprite();
            this.add(this.rankIcon);
            StatUtils.setStatIconInteractions(scene, StatPanelUtils.getCreatureStatPanel(), this.rankIcon, this);
        }

        this.initForces();

        this.setScale(1, 1);
        c.isGlow && this.addGlow();

        if (!!c.effects) {
            for (let eff of c.effects) {
                switch (eff.type) {
                    case CreatureEffectType.FIRED:
                        this.setFireEffect(true);
                        break;
                    case CreatureEffectType.FROZEN:
                        this.setGorgonEffect(true);
                        break;
                }
            }
        }

        for (let skill of c.skills) {
            if (skill.skillType === SkillType.IMMORTALITY) {
                let immortality = CreatureUtils.getUnitImmortality(c);
                if (immortality) {
                    this.initImmortality(immortality);
                }
            } else if (skill.skillType === SkillType.POISON) {
                let poison = CreatureUtils.getUnitPoison(c);
                this.initPoison(poison);
            }
        }
    }

    public syncOriginalPos() {
        this.originalX = this.x;
        this.originalY = this.y;
    }

    private setupRankSprite() {
        let c = this.creature;
        if (!c) {
            return;
        }
        if (CreatureUtils.isPremium(c)) {
            const premiumRankKey = Keys.PREMIUM_RANKS.includes(String(c.level)) ? Keys.ATLAS_PREMIUM_RANKS : Keys.ATLAS_UI;
            const rankIcon = Keys.PREMIUM_RANKS.includes(String(c.level)) ? String(c.level) : Keys.UI_STAT_ENEMY_OVAL;
            SpriteUtils.setSprite(this.scene, this.rankIcon!, premiumRankKey, rankIcon);
            return;
        }
        if (SpriteUtils.BLUE_BORDER.has(c.imageId)) {
            SpriteUtils.setSprite(this.scene, this.rankIcon!, Keys.ATLAS_RANK, 'bl_' + String(c.level));
        } else if (SpriteUtils.RED_BORDER.has(c.imageId)) {
            SpriteUtils.setSprite(this.scene, this.rankIcon!, Keys.ATLAS_RANK, 'red_' + String(c.level));
        } else if (this.isPlayerCreature) {
            if (c.level === 2) {
                SpriteUtils.setSprite(this.scene, this.rankIcon!, Keys.ATLAS_FORCES, 'mine_2');
            } else if (c.level === 3) {
                SpriteUtils.setSprite(this.scene, this.rankIcon!, Keys.ATLAS_FORCES, 'mine_3');
            } else if (c.level === 4) {
                SpriteUtils.setSprite(this.scene, this.rankIcon!, Keys.ATLAS_FORCES, 'mine_4');
            } else if (c.level === 5) {
                SpriteUtils.setSprite(this.scene, this.rankIcon!, 'gold_5');
            } else if (6 <= c.level && c.level <= 25) {
                SpriteUtils.setSprite(this.scene, this.rankIcon!, Keys.ATLAS_RANK, 'gld_' + String(c.level));
            } else {
                SpriteUtils.setSprite(this.scene, this.rankIcon!, Keys.ATLAS_UI, Keys.UI_STAT_MINE_OVAL);
            }
        } else {
            if (c.level === 2) {
                SpriteUtils.setSprite(this.scene, this.rankIcon!, Keys.ATLAS_FORCES, 'enemy_2');
            } else if (c.level === 3) {
                SpriteUtils.setSprite(this.scene, this.rankIcon!, Keys.ATLAS_FORCES, 'enemy_3');
            } else if (c.level === 4) {
                SpriteUtils.setSprite(this.scene, this.rankIcon!, Keys.ATLAS_FORCES, 'enemy_4');
            } else if (c.level === 5) {
                SpriteUtils.setSprite(this.scene, this.rankIcon!, Keys.ENEMY_LEV_5);
            } else if (6 <= c.level && c.level <= 25) {
                SpriteUtils.setSprite(this.scene, this.rankIcon!, Keys.ATLAS_RANK, 'gr_' + String(c.level));
            } else {
                SpriteUtils.setSprite(this.scene, this.rankIcon!, Keys.ATLAS_UI, Keys.UI_STAT_ENEMY_OVAL);
            }
        }
    }

    private initForces() {
        let c = this.creature;
        if (!c) {
            return;
        }
        let isPlayerCreature = this.isPlayerCreature;
        let isPremiumCreature = CreatureUtils.isPremium(c);
        if (c.forcesV2) {
            if (isPremiumCreature) {
                for (let i = 0; i < 12; i++) {
                    let f = c.forcesV2[i]
                    if (!!f) {
                        let wc = f.warriorClass;
                        let force = SpriteUtils.addSpriteN(this.scene, CreatureUtils.getForceIconForTwelveX(i), CreatureUtils.getForceIconForTwelveY(i), Keys.ATLAS_PREMIUM_FORCES, CreatureUtils.getForceIcon(wc, true), 1 / CU.S, 1 / CU.S);
                        this.initForce(c, force, wc);
                    }
                }
            } else if (c.forcesV2.length === 10) {
                for (let i = 0; i < c.forcesV2.length; ++i) {
                    let wc = c.forcesV2[i].warriorClass;
                    let force = SpriteUtils.addSpriteN(this.scene, CreatureUtils.getForceIconForTenX(i), CreatureUtils.getForceIconForTenY(i), CreatureUtils.getForceAtlas(wc),
                        CreatureUtils.getForceIcon(c.forcesV2[i].warriorClass, isPlayerCreature), 1 / CU.S, 1 / CU.S);
                    this.initForce(c, force, wc);
                }
            } else if (c.forcesV2.length > 3) {
                for (let i = 0; i < c.forcesV2.length; ++i) {
                    let wc = c.forcesV2[i].warriorClass;
                    let force = SpriteUtils.addSpriteN(this.scene, CreatureUtils.getForceIconX(i), CreatureUtils.getForceIconY(i), CreatureUtils.getForceAtlas(wc),
                        CreatureUtils.getForceIcon(c.forcesV2[i].warriorClass, isPlayerCreature), 1 / CU.S, 1 / CU.S);
                    this.initForce(c, force, wc);
                }
            } else {
                for (let i = 0; i < c.forcesV2.length; ++i) {
                    let wc = c.forcesV2[i].warriorClass;
                    let force = SpriteUtils.addSpriteN(this.scene, CreatureUtils.getForceIconBigX(i), CreatureUtils.getForceIconBigY(i), CreatureUtils.getForceAtlasBig(wc),
                        CreatureUtils.getForceIcon(c.forcesV2[i].warriorClass, isPlayerCreature), 1 / CU.S, 1 / CU.S);
                    this.initForce(c, force, wc);
                }
            }
        }
    }

    private clearForces() {
        for (let f of this.spriteByForce.values()) {
            f.destroy();
        }
    }

    private initForce(c: Creature, force: Sprite, wc: WarriorClass) {
        this.add(force);
        this.spriteByForce.set(wc, force);
    }

    public setScale(x: number, y?: number): this {
        SpriteUtils.scale(this.attackValueContainer!, 1 / Math.sqrt(x) / CU.S / CU.S);
        SpriteUtils.scale(this.defenceValueContainer!, 1 / Math.sqrt(x) / CU.S / CU.S);
        return super.setScale(x, y ? y : 1);
    }

    public setCallback(callback: () => unknown): void {
        this.callback = callback;
        let self = this;

        this.mainImage!.setInteractive();
        this.mainImage!.on('pointerdown', () => {
            if (this.callback != null) {
                super.setScaleOriginal(this.scaleX * 1.01, this.scaleY * 1.01);
            }
        });
        this.mainImage!.on('pointerup', () => {
            if (this.callback != null) {
                super.setScaleOriginal(this.scaleX / 1.01, this.scaleY / 1.01);
                this.callback();
            }
        });
    }

    public setSmallClickAreaCallback(c:Creature, callback: () => unknown): void {
        this.callback = callback;

        let smallImage: Image;
        if (Keys.ADDITIONAL_CREATURES.includes(c.imageId)) {
            smallImage =  SpriteUtils.addImage(this.scene, 0, 0, Keys.battleCreature(c.imageId), undefined, 0.5 / CU.S, 0.5 / CU.S);
        } else {
            smallImage =  SpriteUtils.addImage(this.scene, 0, 0, AtlasUtils.getCreatureBattleAtlas(c.imageId), c.imageId, 0.5  / CU.S, 0.5  / CU.S);
        }
        this.add(smallImage);
        smallImage.alpha = 0.01;
        smallImage!.setInteractive();
        smallImage!.on('pointerup', () => {
            if (this.callback != null) {
                this.callback();
            }
        });
    }

    public setSkills(skills: Skill[], enableSilentResurrect: boolean): void {
        skills.forEach(s => this.setSkill(s, enableSilentResurrect));
    }

    public refreshForces(creature: Creature) {
        this.creature = creature;
        this.clearForces();
        this.initForces();
        this.setupRankSprite();
    }

    public setCreature(creature: Creature) {
        this.creature = creature;
    }

    public restoreAllForces(restoredForces: Force[]) {
        if (!restoredForces || restoredForces.length === 0) {
            return;
        }
        for (let sp of this.spriteByForce.values()) {
            sp.visible = false;
            sp.destroy();
        }
        this.spriteByForce = new Map<WarriorClass, Phaser.GameObjects.Sprite>();
        let counter = 0;
        for (let i = 0; i < this.creature.forcesV2.length; ++i) {
            let wc = this.creature.forcesV2[i].warriorClass;
            let force = SpriteUtils.addSpriteN(this.scene, CreatureUtils.getForceIconForTenX(i), CreatureUtils.getForceIconForTenY(i), CreatureUtils.getForceAtlas(wc),
                CreatureUtils.getForceIcon(this.creature.forcesV2[i].warriorClass, !!this.creature.playerId), 1 / CU.S, 1 / CU.S);
            this.initForce(this.creature, force, wc);
            counter = i;
        }

        for (let f of restoredForces) {
            let wc = f.warriorClass;
            if (!this.spriteByForce.has(wc)) {
                let force = SpriteUtils.addSpriteN(this.scene, CreatureUtils.getForceIconForTenX(counter), CreatureUtils.getForceIconForTenY(counter), CreatureUtils.getForceAtlas(wc),
                    CreatureUtils.getForceIcon(wc, !!this.creature.playerId), 1 / CU.S, 1 / CU.S);
                this.initForce(this.creature, force, wc);
                ++counter;
            }
        }
    }

    public looseForces(forces: Force[]): void {
        for (let f of forces) {
            let sprite: Sprite | undefined = this.spriteByForce.get(f.warriorClass);
            if (sprite) {
                this.scene.tweens.add({
                    targets: sprite,
                    duration: 2500,
                    props: {
                        y: {value: (sprite.y - 20) * CU.S, ease: 'Linear'},
                        alpha: {value: 0, duration: 2500, ease: 'Linear'}
                    }
                });
            }
        }
    }

    public setSkill(skill: Skill, enableSilentResurrect: boolean) {
        if (skill.skillType === SkillType.HEALTH) {
            this.setHealth(skill, enableSilentResurrect);
        } else if (skill.skillType === SkillType.ATTACK) {
            if (this.attackValue && this.attackValue.active) {
                this.attackValue!.setText(SkillUtils.formatValue(skill.valueCurrent));
            }
        } else if (skill.skillType === SkillType.DEFENCE) {
            if (this.defenceValue && this.defenceValue.active) {
                this.defenceValue!.setText(SkillUtils.formatValue(skill.valueCurrent));
            }
        } else if (skill.skillType === SkillType.IMMORTALITY) {
            this.initImmortality(skill);
        } else if (skill.skillType === SkillType.POISON) {
            this.initPoison(skill);
        }
    }

    public setInkubEffect() {
        this.setKubEffect('mars');
    }

    public setSukkubEffect() {
        this.setKubEffect('venus');
    }

    public setKubEffect(key: string) {
        if (!this.kubEffect) {
            this.kubEffect = SpriteUtils.addSpriteN(this.scene, -2, -94, Keys.ATLAS_EFFECTS, key, 1 / CU.S, 1 / CU.S);
            this.add(this.kubEffect);
            let self = this;

            this.scene.tweens.chain({
                targets: this.kubEffect,
                tweens: [
                    {
                        duration: 1700,
                        props: {
                            y: {value: -105 * CU.S, ease: 'Quadratic.easeInOut'}
                        },
                        yoyo: true,
                        repeat: 1
                    },
                    {
                        duration: 1700,
                        props: {
                            y: {value: -105 * CU.S, ease: 'Quadratic.easeInOut'},
                            alpha: 0
                        },
                        onComplete() {
                            self.kubEffect!.destroy();
                            self.kubEffect = undefined;
                        }
                    },
                ]
            });
            // SoundUtils.addAndPlay(this.scene, Keys.SOUND_MOAN, {volume: Settings.MUSIC_VOLUME});
        }
    }

    public setFireEffect(active: boolean) {
        if (active && !this.fireShell) {
            this.fireShell = this.createOnlayEffect(CreatureUtils.fireEffect(this.creature));
        } else if (!active && this.fireShell) {
            this.fireShell.setVisible(false);
            this.fireShell.destroy();
            this.fireShell = undefined;
        }
    }

    public setGorgonEffect(active: boolean) {
        if (active && !this.gorgonShell) {
            this.gorgonShell = this.createOnlayEffect(CreatureUtils.gorgonEffect(this.creature));
        } else if (!active && this.gorgonShell) {
            this.gorgonShell.setVisible(false);
            this.gorgonShell.destroy();
            this.gorgonShell = undefined;
        }
    }

    private createOnlayEffect(spriteKey: string): Sprite {
        let effect: Sprite = SpriteUtils.addSpriteN(this.scene, 0, 0, Keys.ATLAS_EFFECTS, spriteKey, 1 / CU.S, 1 / CU.S);
        this.add(effect);
        this.bringToTop(effect);
        for (let force of this.spriteByForce.values()) {
            this.bringToTop(force);
        }
        if (this.attackValueContainer) {
            this.bringToTop(this.attackValueContainer);
        }
        if (this.defenceValueContainer) {
            this.bringToTop(this.defenceValueContainer);
        }
        if (this.rankIcon) {
            this.bringToTop(this.rankIcon);
        }
        return effect;
    }

    private initImmortality(skill: Skill) {
        if (!skill) {
            return;
        }
        if (skill.valueCurrent > 0) {

            this.koscheyActive = true;
            this.refreshHealthVisibility();

            if (!this.koscheyBarInner) {
                this.koscheyBarInner = SpriteUtils.addSpriteN(this.scene, 0, 77, Keys.ATLAS_UI, Keys.UI_HEALTH_BAR_INNER, 1 / CU.S, 1 / CU.S);
                this.add(this.koscheyBarInner);
            }
            if (!this.koscheyBarOuter) {
                this.koscheyBarOuter = SpriteUtils.addSpriteN(this.scene, this.koscheyBarInner.x - 30, this.koscheyBarInner.y, Keys.UI_KOSCHEY_BAR_OUTER, undefined, 1, 1)
                    .setOrigin(0, 0.5);
                this.add(this.koscheyBarOuter);
            }
            if (!this.koscheyValue) {
                this.koscheyValue = TextUtils.addText(this.scene, '', 0, 72 / CU.S,
                    '#ffffff', 12 / CU.S, 'normal', 80);
                this.add(this.koscheyValue);
            }

            let total = Math.max(skill.valueCurrent, skill.valueTotal);
            SpriteUtils.setDispSize(this.koscheyBarOuter, skill.valueCurrent == total ? this.HEALTH_BAR_WIDTH : skill.valueCurrent / total * this.HEALTH_BAR_WIDTH, this.HEALTH_BAR_HEIGHT);
            this.koscheyValue.setText(TextUtils.formatMoney(skill.valueCurrent));

        } else {
            // if (this.koscheyShell) {
            //     this.koscheyShell.setVisible(false);
            //     this.koscheyShell.destroy();
            // }

            if (this.koscheyBarInner) {
                this.koscheyBarInner.setVisible(false);
                this.koscheyBarInner.destroy();
                this.koscheyBarInner = undefined;
            }

            if (this.koscheyBarOuter) {
                this.koscheyBarOuter.setVisible(false);
                this.koscheyBarOuter.destroy();
                this.koscheyBarOuter = undefined;
            }

            if (this.koscheyValue) {
                this.koscheyValue.setVisible(false);
                this.koscheyValue.destroy();
                this.koscheyValue = undefined;
            }

            this.koscheyActive = false;
            this.refreshHealthVisibility();
        }
    }

    private initPoison(skill: Skill) {
        if (!skill) {
            return;
        }
        if (skill.valueCurrent > 0) {
            if (!this.poisonPotion) {
                this.poisonPotion = SpriteUtils.addSpriteN(this.scene, 0, 46, Keys.IMG_POISON, undefined, 1 / CU.S, 1 / CU.S);
                this.add(this.poisonPotion);
            }
            if (!this.poisonValue) {
                this.poisonValue = TextUtils.addText(this.scene, '', 0, 51 / CU.S,
                    '#ffffff', 12 / CU.S, 'bold', 80);
                this.add(this.poisonValue);
            }
            if (this.poisonValue && this.poisonValue.active) {
                this.poisonValue.setText(String(skill.valueCurrent));
                this.poisonValue.setText(skill.valueCurrent < 1000000 ? String(skill.valueCurrent) : '∞');
            }
        } else {
            if (this.poisonPotion) {
                this.poisonPotion.setVisible(false);
                this.poisonPotion.destroy();
            }
            if (this.poisonValue) {
                this.poisonValue.setVisible(false);
                this.poisonValue.destroy();
            }
        }
    }

    public setHealth(health: Skill, enableSilentResurrect: boolean) {
        if (this.healthText && this.healthText.active) {
            if (this.infiniteParams) {
                this.healthText.setText('∞');
            } else {
                this.healthText.setText(SkillUtils.formatHealth(health.valueCurrent));
            }

            this.healthBarOuter!.displayWidth = health.valueCurrent == health.valueTotal ? this.HEALTH_BAR_WIDTH : health.valueCurrent / health.valueTotal * this.HEALTH_BAR_WIDTH, this.HEALTH_BAR_HEIGHT;
            // this.healthBarOuter!.displayWidth = health.valueCurrent == health.valueTotal ? this.HEALTH_BAR_WIDTH : health.valueCurrent / health.valueTotal * this.HEALTH_BAR_WIDTH;
            if (health.valueCurrent <= 0) {
                this.onDeadCallback();
            }
            if (enableSilentResurrect && !!this.skull && health.valueCurrent > 0) {
                this.alive = true;
                this.alpha = 1.0;
                this.skull.destroy();
                this.skull = undefined;
            }
        } else {
            console.log('Trying to set health to dead card');
        }
    }

    public setHealthVisible() {
        this.doSetHealthVisible(true);
    }

    public doSetHealthVisible(visible: boolean) {
        this.healthVisible = visible;
        this.refreshHealthVisibility();
    }

    public refreshHealthVisibility() {
        let visible = this.healthVisible;
        if (this.koscheyActive) {
            visible = false;
        }
        this.healthText.setVisible(visible);
        this.healthBarOuter!.setVisible(visible);
        this.healthBarInner!.setVisible(visible);
    }

    public setNameText(name: string): void {
        let y = 84;
        if (this.healthText.visible) {
            y += 10;
        }
        const text = this.scene.add.text(0, y, name,{
            fontSize: '14pt',
            color: '#eeeeee',
            fontFamily: 'Arial',
            align: 'center',
            fontStyle: 'bold',
            wordWrap: { width: 150, useAdvancedWrap: true }
        }).setOrigin(0.5, 0.5);
        text.setLineSpacing(2);
        text.setShadow(-1, 2, '#000000');
        this.add(text);
    }

    public turnOnAttackerLook() {
        this.switchLook(this.playerCardBorder, true, 1.2);
    }

    public turnOnAttackerLookByConsumable() {
        this.turnOnAttackerLook();
        this.isLookByConsumable = true;
    }

    public isDefenderLook(): boolean {
        return this.defenderLook;
    }

    public turnOnDefenderLook() {
        this.switchLook(this.enemyCardBorder, true, 1.2);
        this.defenderLook = true;
    }

    public turnOnDefenderLookByConsumable() {
        this.turnOnDefenderLook();
        this.isLookByConsumable = true;
    }

    public turnOnDefaultLook() {
        if (this.isLookByConsumable) {
            return;
        }
        this.switchLook(this.playerCardBorder, false, 1);
        this.switchLook(this.enemyCardBorder, false, 1);
        this.defenderLook = false;
    }

    public turnOnDefaultLookByConsumable() {
        this.switchLook(this.playerCardBorder, false, 1);
        this.switchLook(this.enemyCardBorder, false, 1);
        this.defenderLook = false;
        this.isLookByConsumable = false;
    }

    public getHealth(): number {
        return CreatureUtils.getUnitHealthCurrentValue(this.getCreature());
    }

    public getCreature() {
        return this.creature;
    }

    public getCardShade() {
        return this.cardShade;
    }

    public setCardShadeVisible() {
        this.cardShade.setVisible(true);
    }

    public showTruncateEffect() {
        this.doShowShield(Keys.IMG_BLOCK_YELLOW);
    }

    public showRedBlock() {
        this.doShowShield(Keys.IMG_BLOCK_RED);
    }

    public showVioletBlock() {
        this.doShowShield(Keys.IMG_BLOCK_VIOLET);
    }

    private doShowShield(img: string) {
        let blockImage = SpriteUtils.addSpriteN(this.scene, this.x ,this.y, img)
            .setDepth(this.depth + 1);
        this.scene.tweens.add({
            targets: blockImage,
            duration: 1300,
            delay: 1600,
            props: {
                scale: { value: 1.5, ease: 'Linear', delay: 1500 },
                alpha: { value: 0, duration: 200, delay: 1500, ease: 'Linear' }
            },
        })
    }

    public showRedHammer(): void {
        this.doShowHammer(Keys.IMG_HAMMER_RED)
    }

    public showVioletHammer(): void {
        this.doShowHammer(Keys.IMG_HAMMER_VIOLET)
    }

    private doShowHammer(img: string): void {
        let hammerImage = SpriteUtils.addSpriteN(this.scene, this.x ,this.y - 30, Keys.ATLAS_EFFECTS, img).setDepth(this.depth + 1);

        this.scene.tweens.add({
            targets: hammerImage,
            y: { start: hammerImage.y, to: hammerImage.y - 80, duration: 600 },
            scale: { start: 1, to: 1.5, duration: 450, delay: 0 },
            alpha: { start: 1, to: 0, duration: 200, delay: 500 },
            repeat: 0
        });
    }

    public showDefendEffect(battleEffect: Effect) {
        let blockImage = SpriteUtils.addSpriteN(this.scene, this.x ,this.y, Keys.ATLAS_UI, CreaturePanel.chooseDefendImage(battleEffect)!)
            .setDepth(this.depth + 1);
        this.scene.tweens.add({
            targets: blockImage,
            duration: 1000,
            delay: 300,
            props: {
                scale: { value: 1.5, ease: 'Linear' },
                alpha: { value: 0, duration: 200, delay: 500, ease: 'Linear' }
            },
        })
    }

    public showDefendPreview(effect: Effect): Sprite {
        let blockImage = SpriteUtils.addSpriteN(this.scene, this.x ,this.y - 70, Keys.ATLAS_UI, CreaturePanel.chooseDefendImage(effect)!, 0.32)
            .setDepth(this.depth + 1).setAlpha(0.01);
        this.scene.tweens.add({
            targets: blockImage,
            duration: 2700,
            props: {
                alpha: { value: 1.0, duration: 2700, ease: 'Linear' }
            },
        });
        return blockImage;
    }

    public die() {
        this.setAlpha(0.5);

        // Принудительно очищаем любую предыдущую интерактивность
        this.removeInteractive();
        // Устанавливаем правильную интерактивность для мертвого существа
        this.setInteractive({
            cursor: 'default',
            draggable: false,
            dropZone: true
        });

        this.scene.time.delayedCall(0, () => {
            if (!this.skull) {
                this.skull = SpriteUtils.addSpriteN(this.scene, this.originalX, this.originalY, Keys.IMG_SKULL_ICON, undefined, 0.8, 0.8)
                    .setAlpha(0.8)
                    .setDepth(this.depth + 1);
            }
        });
        this.alive = false;
    }

    public ensureAlive() {
        if (this.skull) {
            this.alive = true;
            this.alpha = 1.0;
            console.log("SETTING ALIVE HERE");
            this.skull.destroy();
            this.skull = undefined;
        }
    }

    private alive: boolean = true;

    public ressurect() {
        if (!this.alive) {
            this.alive = true;
            let self = this;

            self.removeInteractive();
            self.setInteractive({
                cursor: 'default',
                draggable: true,
                dropZone: true
            });

            if (self.skull) {
                this.scene.tweens.add({
                    targets: self.skull,
                    duration: 500,
                    alpha: 0,
                    onComplete() {
                        if (self.alive) {
                            if (self.skull) {
                                self.skull.destroy();
                                self.skull = undefined;
                            }
                        } else {
                            if (self.skull) {
                                self.skull!.alpha = 0.8;
                            }
                        }
                    }
                });
            }

            this.scene.tweens.add({
                targets: self,
                duration: 500,
                alpha: 1.0,
                onComplete() {
                    if (!self.alive) {
                        self.alpha = 0.5;
                    }
                }
            });
        }
    }

    public setPanelCombative(isCombative: boolean) {
        if (!isCombative) {
            this.setAlpha(0.6);
        } else {
            this.setAlpha(1);
        }
    }

    private switchLook(border: Image, borderVisible: boolean, scale: number): void {
        border.setVisible(borderVisible);
        this.setScale(scale, scale);
    }

    private static chooseDefendImage(battleEffect: Effect) {
        switch (battleEffect) {
            case Effect.BLOCK_BLUE:
                return Keys.IMG_BLOCK_CONTOUR;
            case Effect.BLOCK_CLAN:
                return Keys.IMG_BLOCK_CONTOUR_GREEN;
        }
        return Keys.IMG_BLOCK_CONTOUR;
    }

    private addGlow(): void {
        this.mainImage && this.mainImage.setPipeline("GlowPipelineYellow");
    }

    public getOriginalX(): number {
        return this.originalX;
    }

    public getOriginalY(): number {
        return this.originalY;
    }

    public isAlive(): boolean {
        return this.alive;
    }

}

